import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import {
  Search as SearchIcon,
  X as ClearIcon,
  ArrowUp,
  Plus,
  Loader,
  Clock4,
  Trash2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { RootState } from '../../../../reducers';
import { SUGGESTIONS } from './utils';
import { fetchFiles } from '../../librarySlice';
import { MultiAskAIProps, Stage, LoadingStage } from './types';

import useLimits from '../../../plan/hooks/limits';
import {
  getMultiFileSessions,
  deleteMultiFileSession,
  askMultiFileSummarizedQuestion,
} from '../../../../services/multiFileAskAIService';

import { useQuery, useUpdateQuery } from '../../../../hooks/urlHooks';
import { useUserTags } from '../../../../hooks/useTags';
import { useOutputLanguage } from '../../../../hooks/useOutputLanguage';
import FileList from './FileList';
import Results from './Results';
import FolderSelector from './FolderSelector';

// Sanitize markdown to fix code block formatting
const sanitizeMarkdown = (markdown: string): string => {
  // Remove wrapping markdown code block if present
  let sanitized = markdown.replace(
    /^```\s*(?:markdown)?\n([\s\S]*?)\n```$/g,
    '$1',
  );
  // Remove any other code blocks
  sanitized = sanitized.replace(/```[\s\S]*?```/g, '');
  // Remove any inline code backticks
  sanitized = sanitized.replace(/`[^`]*`/g, '$1');
  // Remove any remaining backticks
  sanitized = sanitized.replace(/`/g, '');
  // Trim any extra whitespace that might have been left
  return sanitized.trim();
};

interface Props extends MultiAskAIProps {
  open?: boolean;
  onClose?: () => void;
}

// Add this type to store selected file information
type CachedFile = {
  _id: string;
  name: string;
};

// Saved conversation structure for history (matches backend SessionListItem)
interface SavedConversation {
  _id: string;
  fileIds: string[];
  conversation: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
}

const MultiAskAI: React.FC<Props> = ({ open = false }) => {
  const { t } = useTranslation();
  const { searchQuery: initialSearchTerm = '' } = useQuery();
  const updateQuery = useUpdateQuery();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [stage, setStage] = useState<Stage>('initial');
  const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [cachedFiles, setCachedFiles] = useState<CachedFile[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    'recents',
  );
  const [isSearching, setIsSearching] = useState(false);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));

  const { files, team, libraryStatus, dispatch } = useSelector(
    (state: RootState) => ({
      files: state.library.files,
      team: state.team.selectedTeam,
      libraryStatus: state.library.status,
      dispatch: useDispatch(),
    }),
  );

  // Auto-select file from URL if present and load initial data
  useEffect(() => {
    const path = window.location.hash;
    const match = path.match(/\/([^/]+)$/);
    if (match && match[1]) {
      const fileId = match[1];
      // Only select if the file exists in the files data
      if (
        !selectedFiles.includes(fileId) &&
        files.data.some((file) => file._id === fileId)
      ) {
        setSelectedFiles([fileId]);

        // Also add to cached files
        const fileToCache = files.data.find((f) => f._id === fileId);
        if (fileToCache) {
          setCachedFiles([{ _id: fileId, name: fileToCache.name }]);
        }
      }
    }
  }, [files.data, selectedFiles]);

  // Add initial data loading effect
  useEffect(() => {
    // Load initial data when component mounts
    if (team?.teamId) {
      dispatch(
        fetchFiles({
          teamId: team.teamId,
          folderId: selectedFolderId || 'recents',
          page: 1,
          limit: 20,
          searchQuery: searchTerm,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        }),
      );
    }
  }, [team?.teamId, selectedFolderId, dispatch]);

  // Reset loaded pages when dependencies change
  useEffect(() => {
    setLoadedPages(new Set([1]));
  }, [team?.teamId, selectedFolderId, searchTerm]);

  const guardLimits = useLimits();
  const [directions, setDirections] = useState('');
  const [activeSource, setActiveSource] = useState<{
    fileId?: string;
    timestamp?: string;
  } | null>(null);
  const [citationCount, setCitationCount] = useState<{ [key: string]: number }>(
    {},
  );
  const { getTag, setTag } = useUserTags();
  const { currentLanguageName } = useOutputLanguage();
  const [copySuccess, setCopySuccess] = useState(false);
  const [resultSearchTerm, setResultSearchTerm] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [lastQuery, setLastQuery] = useState<{
    prompt: string;
    fileIds: string[];
  }>({ prompt: '', fileIds: [] });
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [completion, setCompletion] = useState<string>(
    getTag('multi-ask-ai-last-output') || '',
  );
  const [isLoading, setIsLoading] = useState(false);

  // Handle closing search input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSearchInput && !resultSearchTerm) {
        const target = event.target as Element;
        if (!target.closest('[data-search-container]')) {
          setShowSearchInput(false);
        }
      }
    };

    if (showSearchInput) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showSearchInput, resultSearchTerm]);

  // Handle closing file dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside dropdown AND not on a select dropdown
      if (
        !target.closest('[data-file-dropdown]') &&
        !target.closest('[role="combobox"]') &&
        !target.closest('[role="listbox"]')
      ) {
        setShowFileDropdown(false);
        // Focus the textarea when closing dropdown if files are selected
        if (selectedFiles.length > 0) {
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      }
    };

    if (showFileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showFileDropdown, selectedFiles.length]);

  // Handle closing history dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-history-dropdown]')) {
        setShowHistoryDropdown(false);
      }
    };

    if (showHistoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [showHistoryDropdown]);

  const resetCitationCount = useCallback(() => {
    setCitationCount({});
  }, []);

  const showToast = useCallback(
    (message: string, variant: 'default' | 'destructive' = 'default') => {
      if (variant === 'destructive') {
        toast.error(message);
      } else {
        toast(message);
      }
    },
    [],
  );

  // Load saved conversations from backend on mount
  useEffect(() => {
    const fetchSessions = async () => {
      if (!team?.teamId) return;

      setIsLoadingSessions(true);
      try {
        const response = await getMultiFileSessions(1, 50);
        if (response.success && response.data) {
          setSavedConversations(response.data.sessions);
        }
      } catch (error) {
        console.error('Failed to fetch conversation history:', error);
        showToast('Failed to load conversation history', 'destructive');
      } finally {
        setIsLoadingSessions(false);
      }
    };

    fetchSessions();
  }, [team?.teamId]);

  // Refresh sessions list from backend
  const refreshSessions = useCallback(async () => {
    if (!team?.teamId) return;

    try {
      const response = await getMultiFileSessions(1, 50);
      if (response.success && response.data) {
        setSavedConversations(response.data.sessions);
      }
    } catch (error) {
      console.error('Failed to refresh conversation history:', error);
    }
  }, [team?.teamId]);

  // Note: Session tracking is handled by backend with the summarized endpoint
  // No need for client-side session management

  // Load a conversation from history
  const loadConversation = useCallback(
    (conversationId: string) => {
      const conversationToLoad = savedConversations.find(
        (conv) => conv._id === conversationId,
      );
      if (conversationToLoad) {
        // Find the last user message and assistant response
        const userMessage = conversationToLoad.conversation.find(
          (msg) => msg.role === 'user',
        );
        const assistantMessage = conversationToLoad.conversation
          .slice()
          .reverse()
          .find((msg) => msg.role === 'assistant');

        if (userMessage) {
          setDirections(userMessage.content);
          setLastQuery({
            prompt: userMessage.content,
            fileIds: conversationToLoad.fileIds,
          });
        }

        if (assistantMessage) {
          // Backend now returns citations in correct markdown format [→](fileId@timestamp)
          setCompletion(assistantMessage.content);
        }

        setSelectedFiles(conversationToLoad.fileIds);
        setStage('final');

        // Update cached files
        const restoredCachedFiles = conversationToLoad.fileIds
          .map((fileId) => {
            const fileData = files.data.find((f) => f._id === fileId);
            return fileData
              ? { _id: fileId, name: fileData.name }
              : { _id: fileId, name: 'Unknown File' };
          })
          .filter(Boolean);
        setCachedFiles(restoredCachedFiles);

        setShowHistoryDropdown(false);
      }
    },
    [savedConversations, files.data],
  );

  // Delete a conversation from history
  const deleteConversation = useCallback(
    async (conversationId: string, e?: React.MouseEvent) => {
      e?.stopPropagation();

      try {
        await deleteMultiFileSession(conversationId);
        // Remove from local state immediately for better UX
        setSavedConversations((prev) =>
          prev.filter((conv) => conv._id !== conversationId),
        );
        showToast('Session deleted successfully');
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        showToast('Failed to delete session', 'destructive');
      }
    },
    [],
  );

  const runAskAI = async () => {
    if (selectedFiles.length === 0) {
      showToast(
        t(
          'drawer.multiAskAI.selectAtLeastOne',
          'Please select at least one file to analyze',
        ),
      );
      return;
    }

    try {
      await guardLimits('multipleRecordingAskAI', async () => {
        // Store the current directions before clearing
        const currentDirections = directions.trim();

        // Store the query for potential editing later
        setLastQuery({
          prompt: currentDirections,
          fileIds: [...selectedFiles],
        });

        // Clear input immediately but keep file selection
        setDirections('');

        setStage('processing');
        setCompletion('');
        resetCitationCount();
        setActiveSource(null);

        setLoadingStage('response');
        setIsLoading(true);

        try {
          if (!team?.teamId) {
            throw new Error('Team ID is required');
          }

          // Use the new summarized endpoint
          const response = await askMultiFileSummarizedQuestion(team.teamId, {
            fileIds: selectedFiles,
            question: currentDirections,
          });

          if (response.success && response.data) {
            const sanitizedCompletion = sanitizeMarkdown(
              response.data.answer.content,
            );
            setCompletion(sanitizedCompletion);
            setStage('final');
            setTag({
              key: 'multi-ask-ai-last-output',
              value: sanitizedCompletion,
            });

            // Refresh sessions to show the new conversation
            await refreshSessions();
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          setStage('initial');
          showToast(
            error instanceof Error
              ? error.message
              : t(
                  'drawer.multiAskAI.failedToProcessTranscripts',
                  'Failed to process transcripts. Please try again.',
                ),
            'destructive',
          );
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setLoadingStage(null);
      setDirections('');
    }
  };

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles((prev) => {
      const newSelection = prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId];

      // Update cachedFiles based on the new selection
      if (prev.includes(fileId)) {
        // Remove from cache if deselected
        setCachedFiles((current) =>
          current.filter((file) => file._id !== fileId),
        );
      } else {
        // Add to cache if newly selected
        const fileToAdd = files.data.find((f) => f._id === fileId);
        if (fileToAdd) {
          setCachedFiles((current) => [
            ...current,
            { _id: fileId, name: fileToAdd.name },
          ]);
          // Focus textarea when a file is selected
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 100);
        }
      }

      return newSelection;
    });
  };

  const handleKeyPress = async (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      stage !== 'processing' &&
      selectedFiles.length > 0 &&
      directions.trim() !== ''
    ) {
      event.preventDefault();
      runAskAI();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (selectedFiles.length === 0) {
      showToast(
        t(
          'drawer.multiAskAI.selectRecordingFirst',
          'Please select at least one recording first',
        ),
      );
      return;
    }

    if (stage === 'processing') {
      return;
    }

    // Create a new function that uses the suggestion directly
    const runAskWithSuggestion = async () => {
      if (!suggestion.trim()) return;

      guardLimits('multipleRecordingAskAI', async () => {
        // Store the query for potential editing later
        setLastQuery({
          prompt: suggestion,
          fileIds: [...selectedFiles],
        });

        // Clear input immediately but keep file selection
        setDirections('');

        setCompletion('');
        resetCitationCount();
        setActiveSource(null);

        setStage('processing');
        setLoadingStage('response');
        setIsLoading(true);

        try {
          if (!team?.teamId) {
            throw new Error('Team ID is required');
          }

          // Use the new summarized endpoint
          const response = await askMultiFileSummarizedQuestion(team.teamId, {
            fileIds: selectedFiles,
            question: suggestion,
          });

          if (response.success && response.data) {
            const sanitizedCompletion = sanitizeMarkdown(
              response.data.answer.content,
            );
            setCompletion(sanitizedCompletion);
            setStage('final');
            setTag({
              key: 'multi-ask-ai-last-output',
              value: sanitizedCompletion,
            });

            // Refresh sessions to show the new conversation
            await refreshSessions();
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          setStage('initial');
          showToast(
            error instanceof Error
              ? error.message
              : t(
                  'drawer.multiAskAI.failedToProcessTranscripts',
                  'Failed to process transcripts. Please try again.',
                ),
            'destructive',
          );
        } finally {
          setLoadingStage(null);
          setIsLoading(false);
        }
      });
    };

    runAskWithSuggestion();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    updateQuery({ searchQuery: '' });
    // Reload files without search term
    if (team?.teamId) {
      dispatch(
        fetchFiles({
          teamId: team.teamId,
          folderId: selectedFolderId || 'recents',
          page: 1,
          limit: 20,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        }),
      );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsSearching(true);

    // Use setTimeout to debounce the search
    setTimeout(() => {
      if (team?.teamId) {
        dispatch(
          fetchFiles({
            teamId: team.teamId,
            folderId: selectedFolderId || 'recents',
            page: 1,
            limit: 20,
            searchQuery: newSearchTerm,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          }),
        );
      }
      setIsSearching(false);
    }, 500);
  };

  const handleSourceClick = (fileId: string, timestamp: string) => {
    if (!fileId || !timestamp) {
      // Clear the active source if empty parameters are passed
      setActiveSource(null);
      return;
    }

    setActiveSource((prev) => {
      // If clicking the same source, toggle the preview off
      if (prev?.fileId === fileId && prev.timestamp === timestamp) {
        return null;
      }

      // If clicking a different source, update the source for inline preview
      return { fileId, timestamp };
    });
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const { scrollHeight, scrollTop, clientHeight } = target;

      // Preload when user is getting close to bottom (2 viewport heights before the end)
      const preloadThreshold = clientHeight * 2;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (
        distanceFromBottom <= preloadThreshold && // When user is getting close to bottom
        libraryStatus !== 'pending' && // Not already loading
        files.pagination?.total > files.data.length // More items available
      ) {
        const nextPage = files.pagination.page + 1;
        const maxPages = files.pagination?.total
          ? Math.ceil(files.pagination.total / 20)
          : 0;

        // Only load if we haven't already loaded this page and it's valid
        if (
          team?.teamId &&
          nextPage <= maxPages &&
          !loadedPages.has(nextPage)
        ) {
          // Mark page as being loaded to prevent duplicates
          setLoadedPages((prev) => new Set([...prev, nextPage]));

          dispatch(
            fetchFiles({
              teamId: team.teamId,
              folderId: selectedFolderId || 'recents',
              page: nextPage,
              limit: 20,
              searchQuery: searchTerm,
              sortBy: 'createdAt',
              sortOrder: 'desc',
            }),
          );
        }
      }
    },
    [
      files,
      libraryStatus,
      team?.teamId,
      selectedFolderId,
      dispatch,
      searchTerm,
      loadedPages,
    ],
  );

  // Add folder change handler
  const handleFolderChange = (folderId: string) => {
    setSelectedFolderId(folderId);
    // Don't clear selected files - allow selecting from multiple folders
    // Load files for the selected folder
    if (team?.teamId) {
      dispatch(
        fetchFiles({
          teamId: team.teamId,
          folderId,
          page: 1,
          limit: 20,
          searchQuery: searchTerm,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        }),
      );
    }
  };

  const renderSearchBar = () => (
    <div className="w-full space-y-2">
      <div className="relative">
        {isSearching ? (
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
            <Loader className="animate-spin h-4 w-4 text-primary" />
          </div>
        ) : (
          <SearchIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        )}
        <Input
          type="text"
          placeholder={t(
            'drawer.multiAskAI.searchRecordings',
            'Search recordings...',
          )}
          value={searchTerm}
          onChange={handleSearchChange}
          className="pl-8 pr-8 h-9"
        />
        {searchTerm && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClearSearch}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 h-8 w-8"
          >
            <ClearIcon className="h-3 w-3" />
          </Button>
        )}
      </div>

      <FolderSelector
        onFolderChange={handleFolderChange}
        selectedFolderId={selectedFolderId}
        compact
      />
    </div>
  );

  const handleCopyContent = (cleanedText: string): void => {
    if (cleanedText) {
      navigator.clipboard.writeText(cleanedText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleEditQuery = () => {
    // Restore the last query
    setDirections(lastQuery.prompt);
    setSelectedFiles(lastQuery.fileIds);

    // Update cached files based on restored selection
    const restoredCachedFiles = lastQuery.fileIds
      .map((fileId) => {
        const fileData = files.data.find((f) => f._id === fileId);
        return fileData
          ? { _id: fileId, name: fileData.name }
          : { _id: fileId, name: 'Unknown File' };
      })
      .filter(Boolean);
    setCachedFiles(restoredCachedFiles);

    // DON'T reset stage - keep both results and input visible
    // The stage stays as 'final' so results remain shown

    // Focus the textarea after a brief delay
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  const renderFileList = (compact = false) => (
    <FileList
      files={files}
      selectedFiles={selectedFiles}
      onFileSelect={handleFileSelect}
      onScroll={handleScroll}
      libraryStatus={libraryStatus}
      searchTerm={searchTerm}
      compact={compact}
    />
  );

  const renderInputArea = () => {
    return (
      <div className="p-4">
        <div className="max-w-3xl mx-auto w-full">
          {/* Single rounded container for everything */}
          <div
            className={cn(
              'relative rounded-3xl bg-white dark:bg-background overflow-visible transition-all duration-300 ease-in-out border shadow-sm',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
            )}
          >
            {/* Suggestions Section - At the top with Collapsible */}
            <Collapsible open={selectedFiles.length > 0}>
              <CollapsibleContent className="overflow-hidden transition-all duration-[100ms] data-[state=closed]:animate-[collapsible-up_100ms_ease-out] data-[state=open]:animate-[collapsible-down_100ms_ease-out]">
                <div className="py-2 overflow-x-auto">
                  <div className="flex gap-2 px-3 py-1">
                    {SUGGESTIONS.map((suggestion) => {
                      const isSelected = directions === t(suggestion);

                      return (
                        <button
                          type="button"
                          key={t(suggestion)}
                          onClick={() => handleSuggestionClick(t(suggestion))}
                          disabled={stage === 'processing'}
                          className={cn(
                            'group px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-all relative',
                            isSelected
                              ? 'bg-primary/80 text-primary-foreground pr-6'
                              : 'bg-secondary/50 text-secondary-foreground/70 hover:bg-primary hover:text-primary-foreground hover:pr-6',
                            stage === 'processing' &&
                              'opacity-50 cursor-not-allowed',
                          )}
                        >
                          {t(suggestion)}
                          <ArrowUp
                            className={cn(
                              'absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 transition-all',
                              isSelected
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100',
                            )}
                          />
                        </button>
                      );
                    })}
                    {/* Spacer element for scroll padding */}
                    <div className="w-2 flex-shrink-0" />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Input area with textarea and controls */}
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder={
                  selectedFiles.length === 0
                    ? t(
                        'drawer.multiAskAI.searchPlaceholder.noFiles',
                        'Select files and Ask Anything',
                      )
                    : t(
                        'drawer.multiAskAI.searchPlaceholder.withFiles',
                        `Ask anything about these recordings... (Output: ${currentLanguageName})`,
                      )
                }
                value={directions}
                onChange={(e) => {
                  setDirections(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyPress={handleKeyPress}
                onFocus={(e) => {
                  setIsFocused(true);
                  // Ensure proper height on focus
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onBlur={() => setIsFocused(false)}
                disabled={stage === 'processing'}
                className={cn(
                  'w-full pl-4 pr-12 pt-3 pb-12 min-h-[80px] max-h-[200px] resize-none overflow-y-auto border-0 bg-transparent focus:ring-0 focus:outline-none focus:border-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                )}
                style={{ height: 'auto' }}
              />

              {/* Bottom row with plus button, history button, and file chips */}
              <div
                className="absolute bottom-2 left-2 right-12 flex items-center gap-2"
                data-file-dropdown
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Close history dropdown if it's open
                          if (showHistoryDropdown) {
                            setShowHistoryDropdown(false);
                          }
                          setShowFileDropdown(!showFileDropdown);
                          // Focus textarea when closing dropdown if files are selected
                          if (showFileDropdown && selectedFiles.length > 0) {
                            setTimeout(() => {
                              textareaRef.current?.focus();
                            }, 100);
                          }
                        }}
                        className="h-8 w-8 rounded-full flex-shrink-0 bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Add recordings</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* History button - always visible */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Close file dropdown if it's open
                          if (showFileDropdown) {
                            setShowFileDropdown(false);
                          }
                          setShowHistoryDropdown(!showHistoryDropdown);
                        }}
                        className="h-8 w-8 rounded-full flex-shrink-0 bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70"
                      >
                        <Clock4 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View history</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* File chips next to the plus button */}
                {selectedFiles.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto scrollbar-thin scrollbar-thumb-secondary scrollbar-track-transparent flex-1">
                    {cachedFiles.map((file) => (
                      <Badge
                        key={file._id}
                        variant="secondary"
                        className="group h-8 bg-secondary/50 hover:bg-secondary/80 text-secondary-foreground/70 pl-2 pr-2 group-hover:pr-7 border-0 rounded-full cursor-pointer min-w-0 max-w-[200px] flex-shrink-0 flex items-center relative"
                        onClick={() => {
                          const fileData = files.data.find(
                            (f) => f._id === file._id,
                          );
                          if (fileData) {
                            const baseUrl = window.location.pathname.includes(
                              '/app',
                            )
                              ? '/app/#'
                              : '/#';
                            window.open(
                              `${baseUrl}/library/${fileData.teamId}/recents/${file._id}`,
                              '_blank',
                            );
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const fileData = files.data.find(
                              (f) => f._id === file._id,
                            );
                            if (fileData) {
                              const baseUrl = window.location.pathname.includes(
                                '/app',
                              )
                                ? '/app/#'
                                : '/#';
                              window.open(
                                `${baseUrl}/library/${fileData.teamId}/recents/${file._id}`,
                                '_blank',
                              );
                            }
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${file.name} file`}
                      >
                        <span className="truncate text-xs w-full">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileSelect(file._id);
                          }}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white hover:bg-gray-100 opacity-0 group-hover:opacity-100"
                        >
                          <ClearIcon className="h-3 w-3 text-secondary-foreground" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* File Dropdown menu - positioned above the button */}
                {showFileDropdown && (
                  <Card
                    className="absolute bottom-full mb-2 left-0 z-50 w-96 max-h-96 overflow-hidden"
                    data-file-dropdown
                  >
                    {/* Search bar in dropdown */}
                    <div className="p-4 border-b">{renderSearchBar()}</div>

                    {/* File list in dropdown */}
                    <ScrollArea className="h-64">
                      {renderFileList(true)}
                    </ScrollArea>
                  </Card>
                )}

                {/* History Dropdown menu - positioned above the button */}
                {showHistoryDropdown && (
                  <Card
                    className="absolute bottom-full mb-2 left-10 z-50 w-96 max-h-96 overflow-hidden"
                    data-history-dropdown
                  >
                    {/* Header section matching the + menu style */}
                    <div className="p-4 border-b">
                      <h3 className="text-sm font-medium">
                        {t(
                          'drawer.multiAskAI.recentSessions',
                          'Recent Sessions',
                        )}
                      </h3>
                    </div>

                    {/* Content area in ScrollArea */}
                    <ScrollArea className="h-64">
                      {(() => {
                        if (isLoadingSessions) {
                          return (
                            <div className="flex items-center justify-center py-8">
                              <Loader className="animate-spin h-5 w-5 text-primary" />
                            </div>
                          );
                        }

                        if (savedConversations.length === 0) {
                          return (
                            <div className="flex items-center justify-center py-16 px-8">
                              <p className="text-sm text-muted-foreground text-center">
                                {t(
                                  'drawer.multiAskAI.noHistory',
                                  'No history available yet',
                                )}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-0">
                            {savedConversations.map((conv) => {
                              // Get the first user message as the title
                              const firstUserMsg = conv.conversation.find(
                                (msg) => msg.role === 'user',
                              );
                              const title =
                                firstUserMsg?.content.slice(0, 50) ||
                                'Untitled';

                              const timeAgo = (() => {
                                const diff =
                                  Date.now() -
                                  new Date(conv.updatedAt).getTime();
                                const minutes = Math.floor(diff / 60000);
                                const hours = Math.floor(diff / 3600000);
                                const days = Math.floor(diff / 86400000);

                                if (minutes < 1) return 'Just now';
                                if (minutes < 60) return `${minutes}m ago`;
                                if (hours < 24) return `${hours}h ago`;
                                return `${days}d ago`;
                              })();

                              return (
                                <Card
                                  key={conv._id}
                                  className={cn(
                                    'border-0 rounded-none border-b last:border-b-0 shadow-none transition-colors',
                                  )}
                                >
                                  <CardContent
                                    className="group flex items-center justify-between gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                                    onClick={() => loadConversation(conv._id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        loadConversation(conv._id);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm mb-1 line-clamp-1 font-normal">
                                        {title}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="text-xs text-muted-foreground">
                                          {conv.fileIds.length}{' '}
                                          {conv.fileIds.length === 1
                                            ? 'file'
                                            : 'files'}
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                          •
                                        </span>
                                        <div className="text-xs text-muted-foreground">
                                          {timeAgo}
                                        </div>
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) =>
                                        deleteConversation(conv._id, e)
                                      }
                                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                    </Button>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </ScrollArea>
                  </Card>
                )}
              </div>

              {/* Send button in bottom right */}
              <div className="absolute bottom-2 right-2">
                {stage === ('processing' as Stage) ? (
                  <div className="h-8 w-8 flex items-center justify-center">
                    <Loader className="animate-spin h-5 w-5 text-muted-foreground" />
                  </div>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          onClick={runAskAI}
                          disabled={
                            !directions ||
                            selectedFiles.length === 0 ||
                            stage === 'processing'
                          }
                          size="icon"
                          variant={
                            !directions || selectedFiles.length === 0
                              ? 'ghost'
                              : 'default'
                          }
                          className={cn(
                            'group h-8 w-8 rounded-full transition-all',
                            !directions || selectedFiles.length === 0
                              ? 'bg-muted hover:bg-muted cursor-not-allowed'
                              : 'shadow-sm hover:shadow-md',
                          )}
                          style={{ borderRadius: '50%' }}
                        >
                          <ArrowUp
                            className={cn(
                              'w-4 h-4 transition-transform',
                              !directions || selectedFiles.length === 0
                                ? 'text-muted-foreground'
                                : 'text-white',
                              directions &&
                                selectedFiles.length > 0 &&
                                'group-hover:translate-y-[-1px]',
                            )}
                          />
                        </Button>
                      </TooltipTrigger>
                      {(!directions || selectedFiles.length === 0) && (
                        <TooltipContent>
                          {selectedFiles.length === 0
                            ? 'Select recordings to analyze'
                            : 'Enter a question to ask'}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate if we should show centered state
  const isCenteredState = stage === 'initial' && !completion;

  return open ? (
    <div className="h-full w-full relative overflow-hidden">
      {/* Results Area - Using Collapsible for smooth expansion */}
      <div className="absolute inset-x-0 top-0 bottom-40">
        <Collapsible open={!isCenteredState} className="h-full">
          <CollapsibleContent className="h-full overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.4,0.0,0.2,1)] data-[state=closed]:animate-[collapsible-up_500ms_cubic-bezier(0.4,0.0,0.2,1)] data-[state=open]:animate-[collapsible-down_500ms_cubic-bezier(0.4,0.0,0.2,1)]">
            <div className="flex flex-col h-full">
              <div className="flex-1 px-4 py-4 overflow-y-auto">
                <Results
                  completion={completion}
                  isLoading={isLoading}
                  stage={stage}
                  loadingStage={loadingStage}
                  selectedFiles={selectedFiles}
                  files={files}
                  activeSource={activeSource}
                  citationCount={citationCount}
                  hideReferences={false}
                  handleSourceClick={handleSourceClick}
                  setCitationCount={setCitationCount}
                  resultSearchTerm={resultSearchTerm}
                  setResultSearchTerm={setResultSearchTerm}
                  onCopyContent={handleCopyContent}
                  copySuccess={copySuccess}
                  onClearCompletion={() => {
                    setCompletion('');
                    setStage('initial');
                    setDirections('');
                    setSelectedFiles([]);
                    setCachedFiles([]);
                    setLastQuery({ prompt: '', fileIds: [] });
                    setTag({
                      key: 'multi-ask-ai-last-output',
                      value: '',
                    });
                  }}
                  onEditQuery={handleEditQuery}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Input Area - Animated between center and bottom */}
      <div
        className={cn(
          'absolute inset-x-0 transition-all duration-500 ease-[cubic-bezier(0.4,0.0,0.2,1)]',
          isCenteredState
            ? 'top-1/2 -translate-y-1/2'
            : 'bottom-0 translate-y-0',
        )}
      >
        <div
          className={cn('w-full', isCenteredState ? 'max-w-3xl mx-auto' : '')}
        >
          {renderInputArea()}
        </div>
      </div>
    </div>
  ) : null;
};

export default MultiAskAI;

// Page wrapper component for use in routes
export const MultiAskAIPage: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    // Navigate back to previous page after closing
    window.history.back();
  };

  return (
    <div className="flex w-[calc(100%-32px)] h-[calc(100vh-64px-16px)] m-[8px_16px_8px_16px] mt-0 relative top-0 bg-background rounded-2xl overflow-hidden">
      <MultiAskAI open={isOpen} onClose={handleClose} />
    </div>
  );
};
